import { useState } from 'react'
import { Button } from '@/ui/components'
import {
  getThisWeekend,
  getNextWeekend,
  getNextWeek,
  getWeekFromToday,
  formatDateForAPI
} from '../../utils/dateHelpers'
import { cn } from '@/shared/utils'

export type QuickSelectPreset = 'this-weekend' | 'next-weekend' | 'next-week' | '1-week' | '2-weeks' | null

interface TripDateQuickSelectProps {
  onDateSelect: (startDate: string, endDate: string) => void
  selectedPreset?: QuickSelectPreset | null
  onPresetChange?: (preset: QuickSelectPreset) => void
}

export function TripDateQuickSelect({
  onDateSelect,
  selectedPreset = null,
  onPresetChange
}: TripDateQuickSelectProps) {
  const [activePreset, setActivePreset] = useState<QuickSelectPreset>(selectedPreset)

  const handlePresetClick = (preset: QuickSelectPreset) => {
    setActivePreset(preset)
    onPresetChange?.(preset)

    let dateRange: { start: Date; end: Date }

    switch (preset) {
      case 'this-weekend':
        dateRange = getThisWeekend()
        break
      case 'next-weekend':
        dateRange = getNextWeekend()
        break
      case 'next-week':
        dateRange = getNextWeek()
        break
      case '1-week':
        dateRange = getWeekFromToday(7)
        break
      case '2-weeks':
        dateRange = getWeekFromToday(14)
        break
      default:
        return
    }

    onDateSelect(
      formatDateForAPI(dateRange.start),
      formatDateForAPI(dateRange.end)
    )
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-muted-foreground">Quick Select</label>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant={activePreset === 'this-weekend' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handlePresetClick('this-weekend')}
          className={cn(
            'transition-colors',
            activePreset === 'this-weekend'
              ? 'bg-primary text-white hover:bg-primary/90'
              : 'bg-earth-50 border-earth-700 text-foreground hover:bg-earth-100 dark:bg-earth-200 dark:border-earth-500 dark:text-foreground dark:hover:bg-earth-300'
          )}
        >
          This Weekend
        </Button>
        <Button
          type="button"
          variant={activePreset === 'next-weekend' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handlePresetClick('next-weekend')}
          className={cn(
            'transition-colors',
            activePreset === 'next-weekend'
              ? 'bg-primary text-white hover:bg-primary/90'
              : 'bg-earth-50 border-earth-700 text-foreground hover:bg-earth-100 dark:bg-earth-200 dark:border-earth-500 dark:text-foreground dark:hover:bg-earth-300'
          )}
        >
          Next Weekend
        </Button>
        <Button
          type="button"
          variant={activePreset === 'next-week' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handlePresetClick('next-week')}
          className={cn(
            'transition-colors',
            activePreset === 'next-week'
              ? 'bg-primary text-white hover:bg-primary/90'
              : 'bg-earth-50 border-earth-700 text-foreground hover:bg-earth-100 dark:bg-earth-200 dark:border-earth-500 dark:text-foreground dark:hover:bg-earth-300'
          )}
        >
          Next Week
        </Button>
        <Button
          type="button"
          variant={activePreset === '1-week' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handlePresetClick('1-week')}
          className={cn(
            'transition-colors',
            activePreset === '1-week'
              ? 'bg-primary text-white hover:bg-primary/90'
              : 'bg-earth-50 border-earth-700 text-foreground hover:bg-earth-100 dark:bg-earth-200 dark:border-earth-500 dark:text-foreground dark:hover:bg-earth-300'
          )}
        >
          1 Week
        </Button>
        <Button
          type="button"
          variant={activePreset === '2-weeks' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handlePresetClick('2-weeks')}
          className={cn(
            'transition-colors',
            activePreset === '2-weeks'
              ? 'bg-primary text-white hover:bg-primary/90'
              : 'bg-earth-50 border-earth-700 text-foreground hover:bg-earth-100 dark:bg-earth-200 dark:border-earth-500 dark:text-foreground dark:hover:bg-earth-300'
          )}
        >
          2 Weeks
        </Button>
      </div>
    </div>
  )
}

